/**
 * Baseball Game Constants and Utilities
 */

// Pitch types with their characteristics
export const PITCH_TYPES = {
  FASTBALL: {
    name: 'Fastball',
    speed: 95, // mph
    movement: { x: 0, y: -0.5 },
    color: '#ff4444',
    difficulty: 1
  },
  CURVEBALL: {
    name: 'Curveball', 
    speed: 78,
    movement: { x: 0.3, y: -2 },
    color: '#44ff44',
    difficulty: 2
  },
  SLIDER: {
    name: 'Slider',
    speed: 85,
    movement: { x: 1.5, y: -0.8 },
    color: '#4444ff',
    difficulty: 2
  },
  CHANGEUP: {
    name: 'Changeup',
    speed: 82,
    movement: { x: -0.2, y: -1.2 },
    color: '#ffff44',
    difficulty: 3
  },
  KNUCKLEBALL: {
    name: 'Knuckleball',
    speed: 70,
    movement: { x: 'random', y: 'random' },
    color: '#ff44ff',
    difficulty: 4,
    unlockAchievement: 'curveball_master'
  }
};

// Strike zone dimensions (in game units)
export const STRIKE_ZONE = {
  width: 1.4,  // 17 inches = ~1.4 feet
  height: 2.0, // roughly knee to chest
  depth: 0.1,
  bottom: 1.5, // height from ground
  center: { x: 0, y: 2.5, z: 0 }
};

// Field dimensions
export const FIELD = {
  pitcherMound: { x: 0, y: 0.5, z: -18.4 }, // 60.5 feet from home
  homePlate: { x: 0, y: 0, z: 0 },
  foulLines: 90, // degrees angle
  outfieldWall: 100, // distance in game units (~330 feet)
  wallHeight: 3
};

// Swing timing windows (in ms) - Very forgiving for fun gameplay
export const TIMING = {
  perfect: 150,  // ±150ms = perfect
  good: 250,     // ±250ms = good  
  okay: 350,     // ±350ms = okay contact
  miss: 500      // beyond = miss
};

// Hit outcomes based on timing and contact
export const HIT_OUTCOMES = {
  HOMERUN: { minDistance: 95, exitVelocity: [100, 115], launchAngle: [25, 35] },
  TRIPLE: { minDistance: 80, exitVelocity: [90, 105], launchAngle: [15, 30] },
  DOUBLE: { minDistance: 60, exitVelocity: [85, 100], launchAngle: [10, 25] },
  SINGLE: { minDistance: 30, exitVelocity: [70, 95], launchAngle: [5, 20] },
  OUT: { minDistance: 0, exitVelocity: [50, 85], launchAngle: [-10, 45] }
};

// Achievements
export const ACHIEVEMENTS = {
  FIRST_HIT: {
    id: 'first_hit',
    name: 'First Hit',
    description: 'Get your first base hit',
    icon: '🎯',
    reward: null
  },
  FIRST_HOMERUN: {
    id: 'first_homerun', 
    name: 'Going Yard',
    description: 'Hit your first home run',
    icon: '💣',
    reward: { type: 'bat_size', value: 1.05 } // 5% larger hitbox
  },
  STRIKEOUT_5: {
    id: 'strikeout_5',
    name: 'Strikeout Artist',
    description: 'Strike out 5 batters in one game',
    icon: 'K',
    reward: { type: 'unlock_pitch', value: 'KNUCKLEBALL' }
  },
  WIN_STREAK_3: {
    id: 'win_streak_3',
    name: 'Hot Streak',
    description: 'Win 3 games in a row',
    icon: '🔥',
    reward: { type: 'power_swing', uses: 1 }
  },
  DERBY_CHAMP: {
    id: 'derby_champ',
    name: 'Derby Champion',
    description: 'Win a Home Run Derby',
    icon: '👑',
    reward: { type: 'cosmetic', value: 'golden_bat' }
  },
  PERFECT_GAME: {
    id: 'perfect_game',
    name: 'Perfectionist',
    description: 'Strike out all batters in a game',
    icon: '💎',
    reward: { type: 'cosmetic', value: 'diamond_ball' }
  },
  BATTING_300: {
    id: 'batting_300',
    name: '.300 Hitter',
    description: 'Maintain a .300+ batting average over 20 games',
    icon: '⭐',
    reward: { type: 'contact_boost', value: 1.1 }
  }
};

// Ranked tiers
export const RANKED_TIERS = {
  BRONZE: { name: 'Bronze', minPoints: 0, color: '#cd7f32', icon: '🥉' },
  SILVER: { name: 'Silver', minPoints: 500, color: '#c0c0c0', icon: '🥈' },
  GOLD: { name: 'Gold', minPoints: 1500, color: '#ffd700', icon: '🥇' },
  DIAMOND: { name: 'Diamond', minPoints: 3000, color: '#b9f2ff', icon: '💎' },
  LEGEND: { name: 'Legend', minPoints: 5000, color: '#ff6b6b', icon: '🏆' }
};

// Calculate hit result based on timing and swing angle
export function calculateHitResult(timing, swingAngle, pitchType, powerSwingActive = false) {
  const timingQuality = getTimingQuality(timing);
  const contactQuality = getContactQuality(timingQuality, swingAngle);
  
  if (contactQuality === 'miss') {
    return { result: 'STRIKE', distance: 0, exitVelocity: 0 };
  }
  
  // Base exit velocity - MLB realistic (95-115 mph for well-hit balls)
  let baseVelocity = {
    perfect: 112,  // MLB average HR exit velo is 103-108, barreled balls 105-115
    good: 102,
    okay: 92,
    weak: 78
  }[contactQuality];
  
  // Power swing bonus
  if (powerSwingActive) {
    baseVelocity *= 1.1;
  }
  
  // Random variation (±8 mph)
  const velocity = baseVelocity + (Math.random() - 0.5) * 16;
  
  // Launch angle based on swing angle and timing
  let launchAngle = swingAngle + (timing > 0 ? 5 : -5); // Early = higher, late = lower
  launchAngle += (Math.random() - 0.5) * 8;
  
  // Calculate distance (simplified physics)
  const distance = calculateDistance(velocity, launchAngle);
  
  // Determine outcome
  const outcome = determineOutcome(distance, velocity, launchAngle);
  
  return {
    result: outcome,
    distance: Math.round(distance),
    exitVelocity: Math.round(velocity),
    launchAngle: Math.round(launchAngle),
    contactQuality
  };
}

function getTimingQuality(timing) {
  const absTime = Math.abs(timing);
  // Very forgiving timing - almost always make contact
  if (absTime <= TIMING.perfect) return 'perfect';
  if (absTime <= TIMING.good) return 'good';
  if (absTime <= TIMING.okay) return 'okay';
  if (absTime <= TIMING.miss) return 'okay'; // Even late swings make some contact
  return 'okay'; // Never truly miss - this is HR Derby, not real baseball!
}

function getContactQuality(timingQuality, swingAngle) {
  if (timingQuality === 'miss') return 'miss';
  
  // Swing angle affects contact (-15 to 45 is good range)
  const angleQuality = swingAngle >= -15 && swingAngle <= 45;
  
  if (timingQuality === 'perfect' && angleQuality) return 'perfect';
  if (timingQuality === 'good' && angleQuality) return 'good';
  if (angleQuality) return 'okay';
  return 'weak';
}

function calculateDistance(velocity, angle) {
  // Simplified trajectory calculation
  const radians = angle * (Math.PI / 180);
  const gravity = 9.8;
  const velocityMps = velocity * 0.447; // mph to m/s
  
  // Range formula with air resistance approximation
  const range = (velocityMps * velocityMps * Math.sin(2 * radians)) / gravity;
  const distanceFeet = range * 3.28; // Convert to feet (removed bonus for realism)
  
  // Cap at realistic MLB max distance (~520 ft)
  return Math.min(520, Math.max(0, distanceFeet));
}

function determineOutcome(distance, velocity, angle) {
  // Home run check - must clear the wall!
  // Wall distances: 330 (corners), 375 (gaps), 400 (center)
  // Need proper launch angle (18-42 optimal) and good exit velo
  
  // Moonshot - way over the wall
  if (distance >= 420 && angle >= 18 && angle <= 42) {
    return 'HOMERUN';  // No velo check needed - it's going way out
  }
  // Clear center field comfortably  
  if (distance >= 380 && angle >= 20 && angle <= 40 && velocity >= 95) {
    return 'HOMERUN';
  }
  // Clear the gaps
  if (distance >= 350 && angle >= 18 && angle <= 42 && velocity >= 93) {
    return 'HOMERUN';
  }
  // Just clear the corners (need good angle/velo)
  if (distance >= 335 && angle >= 22 && angle <= 38 && velocity >= 97) {
    return 'HOMERUN';
  }
  
  // Warning track fly out - close but not enough
  if (distance >= 310 && distance < 335 && angle >= 20) {
    return 'OUT'; // Warning track catch
  }
  
  // Ground out
  if (angle < 10 && distance < 200) {
    return Math.random() > 0.5 ? 'SINGLE' : 'OUT';
  }
  
  // Fly out
  if (angle > 42 && distance < 320) {
    return Math.random() > 0.6 ? 'DOUBLE' : 'OUT';
  }
  
  // Gap hits
  if (distance >= 300 && angle >= 15 && angle <= 35) {
    return Math.random() > 0.4 ? 'TRIPLE' : 'DOUBLE';
  }
  
  if (distance >= 250) {
    return Math.random() > 0.3 ? 'DOUBLE' : 'SINGLE';
  }
  
  if (distance >= 150) {
    return Math.random() > 0.4 ? 'SINGLE' : 'OUT';
  }
  
  return 'OUT';
}

// Get player's current rank based on points
export function getRank(points) {
  const tiers = Object.values(RANKED_TIERS).reverse();
  for (const tier of tiers) {
    if (points >= tier.minPoints) {
      return tier;
    }
  }
  return RANKED_TIERS.BRONZE;
}

export default {
  PITCH_TYPES,
  STRIKE_ZONE,
  FIELD,
  TIMING,
  ACHIEVEMENTS,
  RANKED_TIERS,
  calculateHitResult,
  getRank
};
